import React, { useRef, useState } from 'react';
import { FiUpload, FiDownload, FiCheck, FiAlertCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

const ImportExcel = ({
  isOpen,
  onClose,
  onImport,
  title = 'Import từ Excel',
  templateColumns = [],
  templateData = [],
  templateFileName = 'template.xlsx'
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);

      // Preview file
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        setPreview(data.slice(0, 6)); // First 6 rows for preview
      };
      reader.readAsBinaryString(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const response = await onImport(file);
      setResult(response.data);
      if (response.data.success) {
        setFile(null);
        setPreview([]);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.message || t('importExcel.errorOccurred')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([templateColumns, ...templateData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    // Set column widths
    ws['!cols'] = templateColumns.map(() => ({ wch: 20 }));

    XLSX.writeFile(wb, templateFileName);
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setResult(null);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={handleClose} aria-label={t('common.close')}>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Template download */}
          <div style={{
            background: 'var(--bg-color)',
            padding: '16px',
            borderRadius: 'var(--border-radius)',
            marginBottom: '20px'
          }}>
            <p style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>
              📋 {t('importExcel.downloadTemplate')}
            </p>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FiDownload /> {t('importExcel.downloadBtn')}
            </button>
          </div>

          {/* File input */}
          <div
            style={{
              border: '2px dashed var(--border-color)',
              borderRadius: 'var(--border-radius)',
              padding: '30px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'var(--transition)',
              marginBottom: '20px'
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFile = e.dataTransfer.files[0];
              if (droppedFile) {
                fileInputRef.current.files = e.dataTransfer.files;
                handleFileChange({ target: { files: [droppedFile] } });
              }
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
            />
            <FiUpload size={32} style={{ color: 'var(--text-light)', marginBottom: '12px' }} />
            <p style={{ color: 'var(--text-secondary)' }}>
              {file ? (
                <span style={{ color: 'var(--success-color)' }}>
                  ✓ {file.name}
                </span>
              ) : (
                t('importExcel.dragDropHint')
              )}
            </p>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '12px' }}>{t('importExcel.previewTitle')}</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr>
                      {preview[0]?.map((cell, i) => (
                        <th key={i}>{cell}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(1, 5).map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 5 && (
                  <p style={{ color: 'var(--text-light)', fontSize: '13px', marginTop: '8px' }}>
                    {t('importExcel.moreRows', { count: preview.length - 5 })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`alert ${result.success ? 'alert-success' : 'alert-error'}`}>
              {result.success ? <FiCheck /> : <FiAlertCircle />}
              <div>
                <strong>{result.message}</strong>
                {result.data?.errors?.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    <p>{t('importExcel.errorLabel')}</p>
                    <ul style={{ marginLeft: '16px', marginTop: '4px' }}>
                      {result.data.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{t('importExcel.rowError', { row: err.rowIndex, error: err.error })}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>
            {t('importExcel.closeBtn')}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={!file || loading}
          >
            {loading ? t('importExcel.importing') : t('importExcel.importBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportExcel;
